import { Component, OnInit } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';

import { TranslationService } from 'lipp-components';

import {
  LearningSuiteService,
  OidcAuthorizationRequest,
} from 'src/services/learning-suite.service';
import { Utility } from 'src/services/utility.service';

import { ErrorMessage } from 'src/models/Result';

import { InfoPageModule } from 'src/components/info-page/info-page.module';

/**
 * SSO-Bestätigung für externe Plattformen (OIDC-Client, derzeit LearningSuite).
 *
 * Das Backend leitet `GET /Oidc/Authorize` hierher weiter, weil nur das Portal den
 * Login-JWT kennt. Ist der Benutzer eingeloggt, holt die Seite einen Einmal-Code und
 * leitet zum Client zurück; sonst wird die Anfrage gemerkt und nach dem Login
 * fortgesetzt (siehe LoginComponent).
 *
 * Liegt bewusst AUSSERHALB von PagesGuard: der lädt sonst das komplette Portal und
 * leitet ohne Login ohne Rücksprung auf /auth/login.
 */
@Component({
  selector: 'app-sso-authorize',
  templateUrl: './sso-authorize.component.html',
  standalone: true,
  imports: [InfoPageModule],
})
export class SsoAuthorizeComponent implements OnInit {
  public isLoading: boolean = true;

  public icon?: string;
  public title?: string;
  public description?: string;

  constructor(
    private router: Router,
    private translationService: TranslationService,
    private learningSuiteService: LearningSuiteService
  ) {}

  public ngOnInit(): void {
    const search = window.location.search;

    // Bis zum erfolgreichen Rücksprung merken: läuft der JWT ab (401 → Login),
    // setzt die LoginComponent die Anfrage danach fort.
    LearningSuiteService.rememberPendingSsoAuthorize(search);

    if (!Utility.hasAuthToken) {
      this.router.navigateByUrl('/auth/login');
      return;
    }

    const params = new URLSearchParams(search);
    const request: OidcAuthorizationRequest = {
      clientId: params.get('client_id'),
      redirectUri: params.get('redirect_uri'),
      responseType: params.get('response_type'),
      scope: params.get('scope'),
      state: params.get('state'),
      nonce: params.get('nonce'),
      codeChallenge: params.get('code_challenge'),
      codeChallengeMethod: params.get('code_challenge_method'),
    };

    this.learningSuiteService.issueAuthorizationCode(request).subscribe(
      (redirectUrl) => {
        LearningSuiteService.forgetPendingSsoAuthorize();
        window.location.replace(redirectUrl);
      },
      (errorMessages: ErrorMessage[]) => {
        // 401: ApiService leitet bereits auf /auth/login um – Anfrage gemerkt lassen.
        if (errorMessages[0].key === `Messages.HttpError.${HttpStatusCode.Unauthorized}`) {
          return;
        }

        LearningSuiteService.forgetPendingSsoAuthorize();

        this.icon = 'far fa-face-frown';
        this.title = this.translationService.translate('SsoAuthorize.ErrorTitle');
        this.description = this.translationService.translate(
          errorMessages[0].key,
          errorMessages[0].textParameters
        );
        this.isLoading = false;
      }
    );
  }
}
